import React from 'react';
import { ArrowLeft, Check, CheckCircle2 } from 'lucide-react';
import { Button } from '@/ui';

interface StepConfirmProps {
  recipientName: string;
  ccInspectorName: string | null;
  itemCount: number;
  safetyCount: number;
  recommendCount: number;
  totalCredits: number;
  notificationSent: boolean;
  onBack: () => void;
}

export const StepConfirm: React.FC<StepConfirmProps> = ({
  recipientName, ccInspectorName, itemCount, safetyCount, recommendCount, totalCredits,
  notificationSent, onBack,
}) => {
  const recipientFirst = recipientName.split(/\s+/)[0] || 'them';
  const partsForReceipt = [
    `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`,
    `${safetyCount} safety`,
    `${recommendCount} ${recommendCount === 1 ? 'recommendation' : 'recommendations'}`,
    `$${Math.round(totalCredits).toLocaleString('en-US')} requested`,
  ];

  return (
    <main className="max-w-[860px] mx-auto px-4 sm:px-8 pb-24">
      <article className="mt-8 sm:mt-10 rounded-xl border border-border-subtle bg-surface-1 shadow-card p-8 sm:p-12 text-center">
        <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-soft text-brand border border-brand/20">
          <Check className="h-7 w-7" strokeWidth={2.25} aria-hidden />
        </span>
        <div className="mt-5 text-overline uppercase text-brand">{notificationSent ? 'Sent' : 'Saved'}</div>
        <h2 className="mt-2 font-display text-display-md text-ink leading-tight max-w-[24ch] mx-auto">
          {notificationSent ? 'Your fix list is on its way to ' : 'Your fix list was saved for '}
          <span className="italic">{recipientName}</span>
        </h2>
        <p className="mt-3 text-body-md text-ink-subtle max-w-[44ch] mx-auto">
          {notificationSent
            ? `We just emailed ${recipientFirst} — they'll be in touch about next steps. You can re-open this report and send another list any time from the same link.`
            : `We saved your fix list, but couldn't reach ${recipientFirst} by email — please follow up directly.`}
        </p>

        {ccInspectorName && (
          <div className="mt-3.5 text-caption text-ink-subtle">
            CC'd <b className="text-ink font-medium">{ccInspectorName}</b> for the further-inspection item.
          </div>
        )}

        <div className="mt-7 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-surface-2 border border-border-subtle font-mono tabular-nums text-caption text-ink-muted">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
          <span>{partsForReceipt.join(' · ')}</span>
        </div>

        <div className="mt-8 flex justify-center">
          <Button variant="ghost" size="lg" onClick={onBack} leadingIcon={<ArrowLeft />}>
            Back to report
          </Button>
        </div>
      </article>
    </main>
  );
};
